/**
 * Public types + error class for @vibecade/sdk.
 *
 * Everything exported from this file is part of the stable SDK surface.
 * Internal implementation types live in `internal/*`.
 */

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export interface VibecadeConfig {
  /** Your game's slug, e.g. 'pawfall'. */
  gameId: string;
  /** Your game's API key. Pair it with gameId for every mutation. */
  apiKey: string;
  /**
   * Base URL for the platform API. Defaults to the production platform.
   * Override for local dev or staging.
   */
  apiUrl?: string;
  /**
   * Supabase anon key for the target environment. Defaults to the
   * production value baked in at publish time. Required when overriding
   * `apiUrl` to anything other than production.
   */
  anonKey?: string;
  /** When true, the SDK logs internal events via console.debug. */
  debug?: boolean;
  /**
   * When the SDK is embedded inside a launcher iframe, successful
   * `submitScore` and achievement-unlock calls automatically post a
   * message to `window.parent` so the launcher can render a toast.
   * Default: `true`.
   *
   * Set to `false` if your game emits its own
   * `window.parent.postMessage` for these events (pre-0.1.1
   * integrations) — leaving both on produces duplicate toasts and
   * double haptics in the launcher.
   */
  autoHostMessages?: boolean;
  /**
   * Which identity slot this client uses. Defaults to `gameId`.
   *
   * The SDK namespaces Supabase session storage per game so that two
   * instances on one origin — a launcher and the game it embeds — keep
   * separate sessions. That default is wrong for exactly one shape: a
   * single game that owns several `gameId`s.
   *
   * A game with per-difficulty leaderboards needs a `games` row per board,
   * because a board cannot be filtered by anything the client can read
   * back (`scores.meta` is write-only on public reads since migration
   * 0020). Submitting to those boards means a client per board — and on
   * the default, each one finds no session under its own key and signs in
   * as a NEW anonymous player. Ranks, display names and account claiming
   * all fragment, and nothing surfaces it.
   *
   * Point every client at one scope and they share the session:
   *
   * ```ts
   * const main  = new Vibecade({ gameId: 'bloodline', apiKey });
   * const board = new Vibecade({ gameId: 'bloodline-mythic', apiKey,
   *                              authScope: 'bloodline' });
   * ```
   *
   * Two things make sharing safe, and which does what matters. auth-js
   * locks token REFRESH on `lock:${storageKey}` through the Web Locks
   * API, so clients on one scope do not race to spend a single-use
   * refresh token. That lock does NOT cover the check-then-create at
   * first sign-in — reading the session and then creating one are two
   * calls with an await between them — so `init()` serializes that per
   * scope itself. Without both, clients opened together on a fresh page
   * each create a player: the very failure this option exists to
   * prevent, arriving by a different road.
   *
   * Only set this for game ids that genuinely belong to one player.
   */
  authScope?: string;
}

// ---------------------------------------------------------------------------
// Player
// ---------------------------------------------------------------------------

export interface Player {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  /**
   * True when the player signed in anonymously and hasn't yet linked an
   * email or OAuth identity. Flip to false after a successful `identify()`.
   */
  isAnonymous: boolean;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Scores / leaderboard
// ---------------------------------------------------------------------------

export interface ScoreEntry {
  /** Database row id for this score. */
  id: number;
  score: number;
  createdAt: string;
  /**
   * Game-supplied score metadata.
   *
   * **Always `null` on public reads** since SDK 0.1.x — `scores.meta`
   * is restricted to service-role reads via a column-level grant on
   * the backend (the leaderboard view + `getLeaderboard` paths don't
   * request it, and Postgres rejects requests that try). The field
   * stays on this type so games that referenced `entry.meta` keep
   * compiling, but it will never carry data on data returned by
   * `vibecade.scores.getLeaderboard(...)`.
   *
   * If a game needs to read its own meta back (e.g. show the player
   * their previous round summary), that's a future "owner-only reads"
   * SDK surface gated by player id from the JWT — not exposed here.
   */
  meta: Record<string, unknown> | null;
  player: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
  };
  /**
   * 1-based position on the board.
   *
   * A board holds ONE row per player (platform migration 0077): each
   * player's best score in scope, ranked against other players' bests. The
   * platform still stores every submission, but a player who finishes five
   * times holds one slot, not five, and costs everyone below them one rank.
   */
  rank: number;
}

export interface SubmitScoreOptions {
  score: number;
  /**
   * Which board within this game the score belongs to. Omit for a game with
   * one leaderboard.
   *
   * A recurring contest — a daily puzzle, a weekly ladder, a tournament — is a
   * different board each round, but one `games` row. The key is what tells
   * them apart: rank is computed among scores sharing it, and
   * `getLeaderboard({ boardKey })` reads one round.
   *
   * Without it, a daily contest is correct on its first day and wrong from the
   * second, ranking each player against everyone who played a different board.
   * `scope: 'today'` does not help — it is a rolling 24 hours, not a calendar
   * day, so it straddles two rounds.
   *
   * 1-64 characters of letters, digits, dot, underscore, hyphen or colon.
   * The platform never interprets it; it only groups by it.
   *
   * An unkeyed score ranks among unkeyed scores, not among every score the
   * game has taken — so a game that starts segmenting midway does not rank its
   * new rounds against the undifferentiated pile that came before.
   */
  boardKey?: string;
  /** Required when the game has `requires_play_token = true`. */
  playToken?: string;
  /** Arbitrary per-score metadata (run seed, level, etc.). */
  meta?: Record<string, unknown>;
}

export interface SubmitScoreResult {
  accepted: boolean;
  id: number;
  /**
   * The player's position on the board after this submission, under the
   * one-row-per-player rule: one plus the number of OTHER players whose
   * best row sorts ahead of theirs on the same board (a better score, or
   * the same score filed earlier). It is the row number the board shows,
   * and `getLeaderboard({ around: 'me' })` centres on it. A submission below the
   * player's own best does not move them, so the rank returned is the one
   * their best already holds.
   */
  rank: number;
}

export interface LeaderboardOptions {
  /**
   * Read one board within this game. See `SubmitScoreOptions.boardKey`.
   *
   * Omitted reads every score for the game regardless of key, which is the
   * behaviour every unsegmented game already has. Pass the key to read one
   * round of a recurring contest.
   */
  boardKey?: string;
  /**
   * Time window applied to created_at. 'all-time' (default) includes every
   * score; 'today' the last 24 hours; 'weekly' the last 7 days. 'global'
   * is an alias for 'all-time'.
   */
  scope?: 'global' | 'today' | 'weekly' | 'all-time';
  /** Default 20. */
  limit?: number;
  /**
   * When set to 'me', returns `limit` entries centered on the current
   * player's row (their best score in the scope). If the player has no
   * score, falls back to the top of the board.
   */
  around?: 'me';
}

// ---------------------------------------------------------------------------
// Achievements
// ---------------------------------------------------------------------------

export interface AchievementProgress {
  /** Achievement id, e.g. 'FIRST_CUP'. */
  id: string;
  displayName: string;
  description: string | null;
  iconUrl: string | null;
  points: number;
  isProgressive: boolean;
  /** Non-null only when isProgressive. */
  targetProgress: number | null;
  /** 0 for un-started one-shot achievements. */
  progress: number;
  unlocked: boolean;
  unlockedAt: string | null;
}

export interface UnlockResult {
  /**
   * False when the achievement was already unlocked — in that case
   * unlockedAt is the original unlock timestamp, not "now".
   */
  newlyUnlocked: boolean;
  unlockedAt: string;
}

export interface ProgressResult {
  newlyUnlocked: boolean;
  progress: number;
  target: number;
}

// ---------------------------------------------------------------------------
// Cloud save
// ---------------------------------------------------------------------------

/**
 * The cloud-save surface, reached as `vibecade.cloudSave`.
 *
 * Declared as an interface rather than letting the field carry the
 * implementing class, because the class's constructor takes the SDK's internal
 * context — and a public field typed as that class dragged the whole internal
 * type graph, and with it a hard dependency on `@supabase/supabase-js`'s
 * types, into the published `.d.ts`. Consumers whose peer types did not fully
 * resolve then got a type error pointing at THIS package. The runtime object
 * is unchanged; this only narrows what the declarations promise.
 *
 * `T` is unchecked at runtime: whatever was stored comes back parsed, and the
 * SDK does not validate it against the type argument.
 */
export interface CloudSave {
  /** The current player's save for this game, or null if none was written. */
  get<T = unknown>(): Promise<T | null>;
  /** Write (upsert) the current player's save for this game. */
  set<T>(data: T): Promise<void>;
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export type EventProperties = Record<string, unknown>;

// ---------------------------------------------------------------------------
// Identify
// ---------------------------------------------------------------------------

export type IdentifyOptions =
  | { email: string }
  | { provider: 'google' | 'apple' | 'discord' };

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Every failure thrown by the SDK is a `VibecadeError`. `code` is a stable
 * machine-readable identifier (see VibecadeErrorCode); `message` is safe
 * to surface to end users. Raw network and internal error details are
 * never exposed in `message` — when present, they live on `cause`.
 */
export class VibecadeError extends Error {
  readonly code: string;
  readonly cause?: unknown;

  constructor(code: string, message: string, cause?: unknown) {
    super(message);
    this.name = 'VibecadeError';
    this.code = code;
    if (cause !== undefined) this.cause = cause;
  }
}

export const VibecadeErrorCode = {
  // Client-side / lifecycle
  NotInitialized: 'NOT_INITIALIZED',
  InvalidConfig: 'INVALID_CONFIG',
  AuthFailed: 'AUTH_FAILED',
  NetworkError: 'NETWORK_ERROR',
  InternalError: 'INTERNAL_ERROR',

  // Server-returned codes (mirror supabase/functions/_shared/errors.ts)
  MissingAuth: 'MISSING_AUTH',
  InvalidAuth: 'INVALID_AUTH',
  InvalidBody: 'INVALID_BODY',
  InvalidApiKey: 'INVALID_API_KEY',
  GameNotFound: 'GAME_NOT_FOUND',
  AchievementNotFound: 'ACHIEVEMENT_NOT_FOUND',
  AchievementWrongType: 'ACHIEVEMENT_WRONG_TYPE',
  /** Earned-ness condition not met for the player (pen-test Critical #3). */
  AchievementConditionUnmet: 'ACHIEVEMENT_CONDITION_UNMET',
  /** Submitted progress exceeded the per-call delta cap (pen-test High #6). */
  AchievementProgressJumpTooLarge: 'ACHIEVEMENT_PROGRESS_JUMP_TOO_LARGE',
  InvalidPlayToken: 'INVALID_PLAY_TOKEN',
  PlayTokenExpired: 'PLAY_TOKEN_EXPIRED',
  PlayTokenUsed: 'PLAY_TOKEN_USED',
  PlayTokenRequired: 'PLAY_TOKEN_REQUIRED',
  ScoreOutOfRange: 'SCORE_OUT_OF_RANGE',
  // Legacy: no longer emitted — set-achievement-progress silently clamps
  // regressions via greatest() instead of rejecting them (decision #70).
  // Kept exported so existing consumers that switch on it don't break.
  ProgressRegression: 'PROGRESS_REGRESSION',
  TooManyEvents: 'TOO_MANY_EVENTS',
  /**
   * Per-player Edge Function rate limit hit (migration 0022). Status
   * 429. See _shared/rateLimit.ts. Surface as "try again in a moment"
   * — the windows are short (60s for most endpoints, 1h for
   * delete-account).
   */
  RateLimitExceeded: 'RATE_LIMIT_EXCEEDED',
  MethodNotAllowed: 'METHOD_NOT_ALLOWED',

  // PackLens pack integration (migration 0047). The SDK never emits these
  // itself — packlens-cards is server-to-server and bloodline-pack-play is
  // called directly by the game — but the mirror test rightly insists every
  // server-returned code exists here, and a consumer that does call the
  // endpoints raw can switch on them.
  /** Webhook HMAC missing, malformed, mismatched, or stale. Status 401. */
  InvalidSignature: 'INVALID_SIGNATURE',
  /** The consumable board pool ran dry mid-claim. Status 409. */
  BoardPoolExhausted: 'BOARD_POOL_EXHAUSTED',
  /** Unknown (pack_id, card_id) pair. Status 404. */
  PackBoardNotFound: 'PACK_BOARD_NOT_FOUND',
  /**
   * A recorded pack run (migration 0066) that does not exist, belongs to a
   * different pack than the one named, or predates the run-log table.
   * Status 404. Admin surface only; mirrored because every server code is.
   */
  RunLogNotFound: 'RUN_LOG_NOT_FOUND',
  /**
   * A pack proof-photo pathname a replacement retired (migration 0070)
   * offered again. Status 409. Admin surface only; mirrored because every
   * server code is.
   */
  PackPhotoRetired: 'PACK_PHOTO_RETIRED',
  /**
   * The pack board's attempt budget is spent. Status 409. Legacy since
   * migration 0049 replaced the budget with a play window; still mirrored
   * because a stale database can return it.
   */
  AttemptsExhausted: 'ATTEMPTS_EXHAUSTED',
  /**
   * Pack START by a patron whose Patreon tier has no ranked bloodlines left
   * for the UTC month, or grants none (migration 0091). Status 409. The
   * response's `details` carries { tierTitle, allowance, used, monthKey,
   * resetsAt } when the ledger could be read. Every board is closed to the
   * patron until the 1st; free practice is not.
   */
  MonthlyAllowanceExhausted: 'MONTHLY_ALLOWANCE_EXHAUSTED',
  /** The pack's play window (migration 0049) has closed. Status 410. */
  PackExpired: 'PACK_EXPIRED',
  /** Pack FINISH without (or with a consumed) one-time token. Status 401. */
  InvalidFinishToken: 'INVALID_FINISH_TOKEN',
  /**
   * Pack START on a patron-gated pack (migration 0053) with no active patron
   * behind the player's JWT. Status 403. One code for never-linked, lapsed
   * and stale-check alike, because the recovery is the same for all three.
   */
  PatronRequired: 'PATRON_REQUIRED',
  /**
   * Pack FINISH on a patron-gated pack with a token whose START named no
   * patron (migration 0061). Status 403. Not the same as `PATRON_REQUIRED`,
   * which a player answers by connecting Patreon, and not the same as
   * `INVALID_FINISH_TOKEN`, which means the token is spent or unknown: this
   * token is live and cannot write to this board. A report refused with it
   * will be refused identically forever, so drop it rather than retry.
   */
  UnattributedFinish: 'UNATTRIBUTED_FINISH',
  /**
   * Pack START on a pack the creator has not published yet (migration 0057):
   * claimed and linked, awaiting human review on the host's admin panel.
   * Status 403. Browsers are routed to that panel before they can hit this;
   * a game client only sees it by skipping the host's interstitial.
   */
  PackUnpublished: 'PACK_UNPUBLISHED',
  /**
   * Pack FINISH whose token was minted by a pre-publish rehearsal START
   * (migration 0066), arriving after the pack published: the publish wipe
   * promised clean boards, so the report may not write. Status 409. Like
   * `UNATTRIBUTED_FINISH`, refused identically forever - drop, never retry.
   */
  RehearsalWiped: 'REHEARSAL_WIPED',
  /**
   * Pack admin re-deal (migration 0067) refused because the pack is already
   * published: live boards are live contests and are never re-dealt. Status
   * 409. Only the host's admin surface can ever see this; a game client has
   * no path to the action it answers.
   */
  PackAlreadyPublished: 'PACK_ALREADY_PUBLISHED',
  /**
   * Pack FINISH the platform refuses on timing (migration 0076): a scoring
   * run shorter than the floor after its START, or a run log whose step
   * stamps claim more simulated time than the run had of wall time. Status
   * 400. Refused identically forever - drop, never retry.
   */
  ImplausibleTiming: 'IMPLAUSIBLE_TIMING',
  /**
   * Pack admin package_ready (migration 0076) on a crown whose run has no
   * `match` verdict yet. Status 409. Admin surface only, like
   * `PACK_ALREADY_PUBLISHED`.
   */
  WinnerUnverified: 'WINNER_UNVERIFIED',
  /**
   * Pack admin closed_seeds (migration 0076) on a published pack that is
   * still being played or still waiting on its crown. Status 409. Admin
   * surface only.
   */
  PackNotDecided: 'PACK_NOT_DECIDED',
  /**
   * Pack admin void_winner (migration 0076) naming an expected attempt the
   * board's crown no longer wears. Nothing was voided. Status 409. Admin
   * surface only.
   */
  PackCrownChanged: 'PACK_CROWN_CHANGED',
  /**
   * Support queue (migration 0094) given a request id that names no request:
   * already deleted, or never there. Status 404. Admin surface only - the
   * public submit path has no id to name and cannot reach it.
   */
  SupportRequestNotFound: 'SUPPORT_REQUEST_NOT_FOUND',
  /**
   * Pack admin rename (migration 0095) on a pack whose row already breaks
   * another check constraint. Nothing was written. Status 409. Admin surface
   * only.
   */
  PackNotRenamable: 'PACK_NOT_RENAMABLE',

  // Direct-read failures
  CloudSaveFailed: 'CLOUD_SAVE_FAILED',
  LeaderboardFailed: 'LEADERBOARD_FAILED',
  AchievementsFailed: 'ACHIEVEMENTS_FAILED',
} as const;

export type VibecadeErrorCodeType =
  typeof VibecadeErrorCode[keyof typeof VibecadeErrorCode];
